'use client';

import { useCallback } from 'react';

import { Button } from '@naumu/ui/components/button';
import { haptic } from '@naumu/ui/lib/haptics/haptics';
import { cn } from '@naumu/ui/utils/cn';

import { CTA_PRIMARY, CTA_REASSURANCE, CTA_SECONDARY, TAGLINE } from '../constants';
import { useGuestProvisioning } from '@naumu/platform-client/use-guest-provisioning';

/**
 * MarketingCta - the shared end-of-page call to action. One provisioning entry
 * ("Drop your files" / "Start with a sentence") that works from any public
 * page, plus the "No signup, no credit card." reassurance. Minimal styling.
 */
export const MarketingCta = ({ className }: { className?: string }) => {
	const { provisionAndGo, submitting } = useGuestProvisioning();

	const handleStart = useCallback(() => {
		haptic('medium');
		provisionAndGo();
	}, [provisionAndGo]);

	return (
		<div
			className={cn(
				'flex flex-col items-start gap-4 rounded-2xl border border-white/10 bg-white/[0.02] p-8 sm:items-center sm:text-center',
				className,
			)}
		>
			<p className="text-[22px] font-semibold tracking-[-0.01em] text-neutral">{TAGLINE}</p>
			<div className="flex flex-wrap items-center gap-3">
				{/* Both buttons fire the SAME provisioning, so both carry the in-flight
				    state - whichever one was pressed is the one the visitor is
				    watching. `loading` holds each button's width and centres a
				    spinner over its (still-present) label. */}
				<Button
					variant="solid-strong"
					size="md"
					type="button"
					onClick={handleStart}
					loading={submitting}
				>
					{CTA_PRIMARY}
				</Button>
				<Button
					variant="ghost-muted"
					size="md"
					type="button"
					onClick={handleStart}
					loading={submitting}
				>
					{CTA_SECONDARY} →
				</Button>
			</div>
			<p className="text-[13px] text-neutral/40">{CTA_REASSURANCE}</p>
		</div>
	);
};
